import React from 'react';
import Icon from './Icon';
import clsx from 'clsx';

interface PaginationItemProps {
  name?: string | number;
  icon?: string;
  current?: boolean;
  disabled?: boolean;
  onChange?: () => void;
}

const PaginationItem = ({
  name = '',
  icon = '',
  current = false,
  disabled = false,
  onChange,
}: PaginationItemProps) => {
  return (
    <li className="pagination-item">
      <a
        className={clsx(
          current
            ? 'cursor-default bg-gray-100'
            : 'cursor-pointer bg-transparent',
          disabled && 'pointer-events-none',
          'text-md',
          'font-bold',
          'w-10',
          'h-10',
          'flex',
          'justify-center',
          'items-center',
          'rounded'
        )}
        onClick={() => onChange && onChange()}
      >
        {name ? name : null}
        {icon ? <Icon name={icon} className="h-5 w-5" /> : null}
      </a>
    </li>
  );
};

const RenderFirstHalfItems = ({
  intervalStart,
  edgeEntries,
  currentPageIndex,
  onChange,
}) => {
  const renderItems: JSX.Element[] = [];
  const end = Math.min(edgeEntries, intervalStart);
  for (let i = 0; i < end; i++) {
    const publicPageIndex = i + 1;
    renderItems.push(
      <PaginationItem
        key={publicPageIndex}
        name={publicPageIndex}
        current={publicPageIndex === currentPageIndex}
        onChange={() => onChange(publicPageIndex)}
      />
    );
  }
  if (edgeEntries < intervalStart) {
    renderItems.push(
      <PaginationItem key="first_indicator" name="..." disabled={true} />
    );
  }
  return <>{renderItems}</>;
};

const RenderMiddleItems = ({
  intervalStart,
  intervalEnd,
  currentPageIndex,
  onChange,
}) => {
  const renderItems: JSX.Element[] = [];
  for (let i = intervalStart; i < intervalEnd; i++) {
    const publicPageIndex = i + 1;
    renderItems.push(
      <PaginationItem
        key={publicPageIndex}
        name={publicPageIndex}
        current={publicPageIndex === currentPageIndex}
        onChange={() => onChange(publicPageIndex)}
      />
    );
  }
  return <>{renderItems}</>;
};

const RenderSecondHalfItems = ({
  intervalEnd,
  edgeEntries,
  pageCount,
  currentPageIndex,
  onChange,
}) => {
  const renderItems: JSX.Element[] = [];
  if (pageCount - edgeEntries > intervalEnd) {
    renderItems.push(
      <PaginationItem key="second_indicator" name="..." disabled={true} />
    );
  }
  const begin = Math.max(pageCount - edgeEntries, intervalEnd);
  for (let i = begin; i < pageCount; i++) {
    const publicPageIndex = i + 1;
    renderItems.push(
      <PaginationItem
        key={publicPageIndex}
        name={publicPageIndex}
        current={publicPageIndex === currentPageIndex}
        onChange={() => onChange(publicPageIndex)}
      />
    );
  }
  return <>{renderItems}</>;
};

const Pagination = ({ model, onChange }) => {
  const {
    currentPageIndex,
    pageCount,
    intervalStart,
    intervalEnd,
    edgeEntries,
  } = model;

  return (
    <>
      {pageCount > 1 && (
        <ul className="flex justify-center">
          {currentPageIndex > 1 && (
            <PaginationItem
              icon="chevron-left"
              current={false}
              disabled={false}
              onChange={() => onChange(currentPageIndex - 1)}
            />
          )}
          {intervalStart > 0 && edgeEntries > 0 && (
            <RenderFirstHalfItems
              intervalStart={intervalStart}
              edgeEntries={edgeEntries}
              currentPageIndex={currentPageIndex}
              onChange={onChange}
            />
          )}
          <RenderMiddleItems
            intervalStart={intervalStart}
            intervalEnd={intervalEnd}
            currentPageIndex={currentPageIndex}
            onChange={onChange}
          />
          {intervalEnd < pageCount && edgeEntries > 0 && (
            <RenderSecondHalfItems
              intervalEnd={intervalEnd}
              pageCount={pageCount}
              edgeEntries={edgeEntries}
              currentPageIndex={currentPageIndex}
              onChange={onChange}
            />
          )}
          {currentPageIndex < pageCount && (
            <PaginationItem
              icon="chevron-right"
              current={false}
              disabled={false}
              onChange={() => onChange(currentPageIndex + 1)}
            />
          )}
        </ul>
      )}
    </>
  );
};

export default Pagination;
